import React, { ChangeEvent, useEffect, useState } from "react";
import { Form, Col, Row } from "react-bootstrap";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import Title from "../../../components/Admin/Title";
import useIsAdmin from "../../../hooks/useIsAdmin";
import ErrorPage from "../../ErrorPage";
import Button from "../../../components/Button";
import { useUser } from "../../../components/UserContext";
import LoadingContent from "../../../components/LoadingContent";

interface GP {
  name: string;
  id: number;
  press_conference: string;
  pole_video: string;
  gp_video: string;
}

const ModifyResult = () => {
  const { access } = useIsAdmin();
  document.title = "LVF1 - Modify Result";
  const { user } = useUser() as any;
  const token = window.localStorage.getItem("userLogged");
  const [result, setResult] = useState("");
  const [validated, setValidated] = useState(false);

  const [selectedGP, setSelectedGP] = useState(-1);

  const [uploading, setUploading] = useState(false);

  const [lastGrandsPrix, setLastGrandsPrix] = useState<GP[]>();
  const [assignments, setAssignments] = useState<
    {
      id: number;
      assignment_lvf1: {
        driver: { username: string };
        number: number;
        team_championship: { team: { name: string } };
      };
    }[]
  >([]);
  const [values, setValues] = useState<{
    id: number;
    assignment: string | undefined;
    laps_lead: string | undefined;
    time: string | undefined;
    position: string | undefined;
  }>({ id: -1, assignment: "", laps_lead: "0", time: "", position: "" });
  const [results, setResults] = useState<
    {
      id: number;
      assignment: {
        assignment_lvf1: {
          driver: { username: string };
        };
      };
      position: string | undefined;
      time: string | undefined;
      laps_lead: string | undefined;
      gp_race_event_id: string;
      assignment_id: string;
    }[]
  >([]);

  useEffect(() => {
    axios
      .get(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/grandprix/getLastGrandsPrix`,
      )
      .then(({ data }) => {
        setSelectedGP(data.nextGrandsPrix[0].id);
        setLastGrandsPrix(data.nextGrandsPrix);
      })
      .catch(({ response }) => {
        throw new Error(response);
      });
  }, []);

  useEffect(() => {
    if (selectedGP != -1) {
      axios
        .get(
          `${
            process.env.NODE_ENV === "development"
              ? "http://localhost:5500"
              : "https://ligavirtualf1.es:5500"
          }/api/grandprix/getResultsAndAssignments`,
          {
            params: {
              grand_prix_event_id: selectedGP,
            },
          },
        )
        .then(({ data }) => {
          setAssignments(data.assignments);
          setResults(data.results);
        })
        .catch(({ response }) => {
          throw new Error(response);
        });
    }
  }, [selectedGP]);

  const submitForm = (event: any) => {
    if (token == null) return;
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;

    if (!(form !== null && form.checkValidity() === false)) {
      setUploading(true);
      axios
        .post(
          `${
            process.env.NODE_ENV === "development"
              ? "http://localhost:5500"
              : "https://ligavirtualf1.es:5500"
          }/api/admin/modifyRaceResult`,
          {
            user,
            token: JSON.parse(token).token,
            values,
          },
        )
        .then(() => {
          toast.success("Data modified succesfully");
          setUploading(false);
          setValidated(false);
        })
        .catch(({ response }) => {
          toast.error("Error");
          setUploading(false);
          setValidated(false);
          throw new Error(response);
        });
    }
    setValidated(true);
  };

  const inputChange = ({ target }: { target: any }) => {
    const { name, value } = target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  if (!access) return <ErrorPage />;
  if (lastGrandsPrix == null) return <LoadingContent />;
  return (
    <div className="full-height padding-md app-container flex-center-top page-transition">
      <div className="form-container">
        <Title text="Modify Result" />
        <small className="mandatory-fields">(*) Required fields</small>
        <Form noValidate validated={validated} onSubmit={submitForm}>
          <Row className="mb-3">
            <Form.Group className="form-group" as={Col} md="6">
              <Form.Label>Grand Prix (*)</Form.Label>
              <Form.Select
                name="id"
                required
                value={selectedGP}
                onChange={(e) => {
                  inputChange(e);
                  setSelectedGP(parseInt(e.target.value));
                }}
              >
                {lastGrandsPrix.map((gp) => {
                  return (
                    <option key={gp.id} value={gp.id}>
                      {gp.name}
                    </option>
                  );
                })}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Field required
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="form-group" as={Col} md="6">
              <Form.Label>Result (*)</Form.Label>
              <Form.Select
                required
                value={result}
                onChange={(e: any) => {
                  const selectedId = e.target.value;
                  setResult(selectedId);
                  const selectedResult = results.find(
                    (r) => r.id == selectedId,
                  );
                  if (selectedResult) {
                    setValues({
                      id: selectedResult.id,
                      position:
                        selectedResult.position == null
                          ? ""
                          : selectedResult.position,
                      assignment: selectedResult.assignment_id,
                      laps_lead: selectedResult.laps_lead,
                      time:
                        selectedResult.time == null ? "" : selectedResult.time,
                    });
                  }
                }}
              >
                <option value="">None</option>
                {results.map((result) => {
                  try {
                    return (
                      <option value={result.id} key={result.id}>
                        {`${
                          result.assignment.assignment_lvf1.driver.username
                        } ${
                          result.position != null ? result.position + "º" : ""
                        } [id: ${result.gp_race_event_id}]`}
                      </option>
                    );
                  } catch (er) {}
                })}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Field required
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="form-group" as={Col} md="5">
              <Form.Label>Driver (*)</Form.Label>
              <Form.Select
                name="assignment"
                required
                value={values.assignment}
                onChange={(e) => inputChange(e)}
              >
                <option value="">None</option>
                {assignments.map((assignment) => {
                  try {
                    return (
                      <option
                        value={assignment.id}
                        key={"ass-" + assignment.id}
                      >
                        {`#${assignment.assignment_lvf1.number} ${assignment.assignment_lvf1.driver.username} (${assignment.assignment_lvf1.team_championship.team.name})`}
                      </option>
                    );
                  } catch {}
                })}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Field required
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="form-group" as={Col} md="2">
              <Form.Label>
                Position
                <span
                  className="profile-bubble"
                  data-tooltip="Do not make numerical position changes! Won't update points"
                  data-flow="right"
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="label-icon"
                  />
                </span>
              </Form.Label>
              <Form.Control
                name="position"
                type="number"
                onChange={(e) => inputChange(e)}
                value={values?.position}
              />
              <Form.Control.Feedback type="invalid">
                Field required
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="form-group" as={Col} md="2">
              <Form.Label>Laps Lead (*)</Form.Label>
              <Form.Control
                name="laps_lead"
                type="number"
                required
                onChange={(e) => inputChange(e)}
                value={values?.laps_lead}
              />
              <Form.Control.Feedback type="invalid">
                Field required
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="form-group" as={Col} md="3">
              <Form.Label>
                Time
                <span
                  className="profile-bubble"
                  data-tooltip="NC, INJ, EX, DSQ, DNS, DNQ, DNF..."
                  data-flow="right"
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="label-icon"
                  />
                </span>
              </Form.Label>
              <Form.Control
                name="time"
                type="string"
                onChange={(e) => inputChange(e)}
                value={values?.time}
              />
              <Form.Control.Feedback type="invalid">
                Field required
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <div className="text-center">
            {uploading ? (
              <FontAwesomeIcon icon={faSpinner} className="spinner-button" />
            ) : (
              <Button text={"Modify"} type="submit" />
            )}
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ModifyResult;
